import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Box, Typography, Card, CardContent, Tabs, Tab, CircularProgress, ButtonGroup, Button, IconButton, Snackbar, Alert, Chip } from '@mui/material';
import { styled } from '@mui/material/styles';
import BackWithBreadcrumbAppbar from '../components/BackWithBreadcrumbAppbar';
import { useTranslation } from 'react-i18next';
import OWWLogo from '../Assets/OWW-Logo4.png';

import BookmarkAddIcon from '@mui/icons-material/BookmarkAdd';
import BookmarkAddedIcon from '@mui/icons-material/BookmarkAdded';
import useUserData from '../hooks/useUserData';
import { gql, useMutation, useQuery } from '@apollo/client';

const HeaderSection = styled(Box)(() => ({
    background: '#2D4356',
    position: 'relative',
    overflow: 'hidden',
    paddingBottom: '30px',
    width: '100%',
    '&::after': {
        content: '""',
        position: 'absolute',
        bottom: 0,
        left: '50%',
        width: '200%',
        height: '60px',
        background: '#2D4356',
        borderBottomLeftRadius: '50%',
        borderBottomRightRadius: '50%',
        transform: 'translateX(-50%) scaleX(0.8)'
    }
}));

const DaySection = styled(Box)(() => ({
    background: '#1aa98b',
    position: 'relative',
    padding: '20px 0 40px',
    marginTop: '-60px',
    overflow: 'hidden',
    width: '100%',
    '&::after': {
        content: '""',
        position: 'absolute',
        bottom: '-20px',
        left: '50%',
        width: '200%',
        height: '60px',
        background: '#1aa98b',
        borderBottomLeftRadius: '100%',
        borderBottomRightRadius: '100%',
        transform: 'translateX(-50%) scaleX(0.85)'
    }
}));

const SessionCard = styled(Card)(() => ({
    margin: '1rem auto',
    backgroundColor: '#F8F9FA',
    borderRadius: '16px',
    overflow: 'hidden',
    boxShadow: 'none',
    maxWidth: '600px'
}));

const OpeningCeremonyCard = styled(Card)(() => ({
    margin: '1rem auto',
    backgroundColor: '#F8F9FA',
    borderRadius: '16px',
    overflow: 'hidden',
    boxShadow: 'none',
    maxWidth: '600px'
}));

const SessionHeader = styled(Box)(() => ({
    background: '#2D4356',
    color: 'white',
    padding: '0.75rem 1.25rem',
    borderRadius: '16px',
    maxWidth: '65%',
    width: 'fit-content',
    marginLeft: 'auto',
    textAlign: 'left'
}));

const SessionTitle = styled(Typography)(() => ({
    fontSize: '1.1rem',
    fontWeight: 600,
    color: '#000',
    marginBottom: '0.5rem'
}));

const SessionTime = styled(Typography)(() => ({
    color: '#000',
    fontSize: '0.9rem',
    fontWeight: 500
}));

const ChairLabel = styled(Typography)(() => ({
    fontSize: '0.9rem',
    color: '#000',
    marginBottom: '0.25rem'
}));

const ChairName = styled(Typography)(() => ({
    color: '#00B8A9',
    fontSize: '0.9rem',
    fontWeight: 600,
    marginBottom: '0.25rem'
}));

const ChairDetails = styled(Typography)(() => ({
    color: '#000',
    fontSize: '0.9rem',
    marginBottom: '0.15rem'
}));

const CeremonyTitle = styled(Typography)(() => ({
    color: '#00B8A9',
    fontSize: '1.25rem',
    fontWeight: 700
}));

const CeremonyTime = styled(Typography)(() => ({
    color: '#000000',
    fontSize: '0.9rem',
    fontWeight: 700
}));

const StyledTabs = styled(Tabs)(({ theme }) => ({
    backgroundColor: '#fff',
    borderRadius: '30px',
    padding: '5px',
    margin: '0 auto',
    width: 'fit-content',
    position: 'relative',
    zIndex: 2,
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    '& .MuiTabs-indicator': {
        display: 'none',
    },
}));

const StyledTab = styled(Tab)(({ theme }) => ({
    borderRadius: '25px',
    margin: '0 5px',
    minWidth: '120px',
    transition: 'all 0.3s ease',
    '&.Mui-selected': {
        backgroundColor: '#2D4356',
        color: '#fff',
    },
}));

const PanelistSection = styled(Box)(() => ({
    marginTop: '1.5rem',
    paddingTop: '1.5rem',
    borderTop: '2px solid #eee'
}));

const PanelistGrid = styled(Box)(() => ({
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: '1.5rem',
    marginTop: '1rem'
}));

const PanelistCard = styled(Box)(() => ({
    textAlign: 'center',
    padding: '1rem',
    borderRadius: '8px',
    backgroundColor: '#fff',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
}));

const PanelistImage = styled('img')(() => ({
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    objectFit: 'cover',
    marginBottom: '0.5rem',
    border: '2px solid #00B8A9'
}));

export const GET_SAVED_AGENDA = gql`
  query GetSavedAgenda {
    getSavedAgenda {
        id
        agendaName
        day
        date
        location
        sessionDetail {
          id
          session_name
          session_heading
          session_type
          session_time
          session_title
          session_image
          session_chairedBy
          session_chared_name
          session_chaired_designation
          session_chaired_company
          session_panelist {
            panelist_by
            panelist_name
            panelist_designation
            panelist_company
            panelist_image
          }
      }
    }
  }
`;



export const SAVE_SESSION = gql`
  mutation SaveSession($session: SessionInput!){
    saveSession(session: $session) {
      success
      message
      session {
      id
      agendaName
      day
      date
      location
      sessionDetail {
        id
        session_name
        session_heading
        session_type
        session_time
        session_title
        session_image
        session_chairedBy
        session_chared_name
        session_chaired_designation
        session_chaired_company
        session_panelist {
          panelist_by
          panelist_name
          panelist_designation
          panelist_company
          panelist_image
        }
      }
}
    }
  }
`;

const REMOVE_SESSION = gql`
  mutation RemoveSession($sessionId: ID!) {
    removeSession(sessionId: $sessionId) {
      success
      message
    }
  }
`;



const AgendaUI = ({ data, showDateHeader = true }) => {
    const { t } = useTranslation();
    const [agendaData, setAgendaData] = useState([]);
    const [currentDay, setCurrentDay] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState(false);
    const [savedAgenda, setSavedAgenda] = useState([]);
    const { data: user_data, refetch: userRefetch } = useUserData();
    let userdata = user_data?.me;
    if (typeof user_data?.me === 'string') {
        userdata = JSON.parse(user_data?.me);
    }

    const user = userdata;

    const ErrorAlert = styled(Alert)(({ theme }) => ({
        backgroundColor: '#fff1f0',
        color: '#ff4d4f',
        border: '1px solid #ffccc7',
        borderRadius: '8px',
        alignItems: 'center',
        '& .MuiAlert-icon': {
            color: '#ff4d4f',
        },
    }));
    const { data: savedData } = useQuery(GET_SAVED_AGENDA);
  const [saveSession] = useMutation(SAVE_SESSION);
  const [removeSession] = useMutation(REMOVE_SESSION);
    useEffect(() => {

        if (showDateHeader) {
            // Process data only for original agenda view
            const processedData = data.map(day => ({
                ...day,
                sessionDetail: day.sessionDetail.map(session => ({
                    ...session,
                    day: day.day,
                    date: day.date,
                    location: day.location,
                    session_image: session.session_image === 'OWW-Logo4' ? OWWLogo : session.session_image,
                    agendaName: day.agendaName,

                    id: `${session.id}`
                }))
            }));
            setAgendaData(processedData);
        } else {

            setAgendaData(data);
        }

    }, [data]);
    const savedSessions = useMemo(() => {
        const map = new Map();
        if (savedData?.getSavedAgenda) {
          savedData.getSavedAgenda.forEach(item => {
            map.set(item.sessionDetail.id, item);
          });
        }
        return map;
      }, [savedData]);

    const toggleAgendaItem = async (session) => {
        if (user?.role !== 'delegate') {
            setSnackbarMessage(
                <span>
                    Agenda saving is exclusively available for registered delegates. <br />
                    To upgrade your registration or for assistance, please contact our team at<br />
                    <strong>
                        <a href="mailto:sarah@rayaservices.com">sarah@rayaservices.com</a>
                    </strong> or call <strong>
                        <a href="tel:+96891249107">+968 9124 9107</a>
                    </strong>.<br />
                    Or <a href="https://eventegration.com/oww-delegate-online-2025/" target="_blank" rel="noopener noreferrer">click here</a> to register.
                </span>
            );
            setSnackbarOpen(true);
            return;
        }

        try {
            let isSaved;
            if (savedSessions.has(session.id)) {
              await removeSession({
                variables: { sessionId: savedSessions.get(session.id).id },
                update: (cache) => {
                  cache.modify({
                    fields: {
                      getSavedAgenda(existing, { readField }) {
                        isSaved = true;
                        return existing.filter(item => 
                          readField('id', item) !== savedSessions.get(session.id).id
                        );
                      }
                    }
                  });
                }
              });
            } else {
              await saveSession({
                variables: {
                  session: {
                    id: session.id,
                    agendaName: session.agendaName,
                    day: session.day,
                    date: session.date,
                    location: session.location,
                    sessionDetail: {
                      ...session,
                      session_panelist: session.session_panelist?.map(p => ({
                        ...p,
                        __typename: undefined
                      }))
                    }
                  }
                },
                update: (cache, { data: { saveSession } }) => {
                  cache.modify({
                    fields: {
                        
                      getSavedAgenda(existing = []) {
                        isSaved = false;
                        return [...existing, saveSession.session];
                      }
                    }
                  });
                }
              });
            }

            setSnackbarMessage(isSaved ? t('sessionRemovedFromAgenda') : t('sessionAddedToAgenda'));
            
            setSnackbarOpen(true);
        } catch (error) {
            console.error('Agenda update error:', error);
            setSnackbarMessage(t('errorUpdatingAgenda'));
            setSnackbarOpen(true);
        }
    };
    const isSessionSaved = useCallback(
        (sessionId) => savedSessions.has(sessionId),
        [savedSessions]
      );
    // const handleCloseSnackbar = () => {
    //     setSnackbarOpen(false);
    //     setAddedSessionId(null);
    // };

    if (!agendaData.length) return null;
    const currentDayData = agendaData[currentDay];



    // if (isLoading) {
    //   return (
    //     <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
    //       <CircularProgress />
    //     </Box>
    //   );
    // }


    return (
        <>
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={() => setSnackbarOpen(false)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                {user?.role !== 'delegate' ? (
                    <ErrorAlert
                        severity="error"
                        sx={{ width: '100%', maxWidth: '400px' }}
                        onClose={() => setSnackbarOpen(false)}
                    >
                        <Typography variant="body2" component="div">
                            {snackbarMessage}
                        </Typography>
                    </ErrorAlert>
                ) : (
                    <Alert
                        severity={snackbarMessage === t('sessionAddedToAgenda') ? 'success' : 'info'}
                        sx={{ width: '100%' }}
                        onClose={() => setSnackbarOpen(false)}
                    >
                        {snackbarMessage}
                    </Alert>
                )}
            </Snackbar>

            {showDateHeader && <HeaderSection>
                <Typography variant="h4" sx={{ color: '#fff', textAlign: 'center', my: 3 }}>
                    {currentDayData?.agendaName}
                </Typography>

                <StyledTabs sx={{ mb: 2 }} value={currentDay} onChange={(_, newValue) => setCurrentDay(newValue)} centered>
                    {agendaData.map((day, index) => (
                        <StyledTab key={index} label={`Day ${day.day}`} />
                    ))}
                </StyledTabs>
            </HeaderSection>}

            {showDateHeader && <DaySection>
                <Typography variant="h4" sx={{ color: '#fff', textAlign: 'center' }}>
                    {currentDayData?.date}<br />
                    {currentDayData?.location}
                </Typography>
            </DaySection>}


            <Box sx={{ px: 2, mt: 5 }}>
                {currentDayData?.sessionDetail.map((session, index) => (
                    session.type === "info" ? (
                        <OpeningCeremonyCard key={index}>
                            <CardContent sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Box
                                    component="img"
                                    src={session.session_image || OWWLogo}
                                    sx={{
                                        width: 60,
                                        height: 60,
                                        borderRadius: '12px',
                                        backgroundColor: '#2D4356'
                                    }}
                                />
                                <Box>
                                    {session.session_name && <Chip label={session.session_name} color="primary" size="small" />}
                                    <CeremonyTitle>
                                        {session.session_name}
                                    </CeremonyTitle>
                                    <CeremonyTime>
                                        {session.session_time}
                                    </CeremonyTime>
                                </Box>
                                {session.session_panelist && session.session_panelist.length > 0 && (
                                    <PanelistSection>
                                        <Typography variant="h6" sx={{
                                            color: '#2D4356',
                                            fontWeight: 600,
                                            mb: 2
                                        }}>
                                            Panelists
                                        </Typography>
                                        <PanelistGrid>
                                            {session.session_panelist.map((panelist, panelIndex) => (
                                                <PanelistCard key={panelIndex}>
                                                    <PanelistImage
                                                        src={panelist.panelist_image}
                                                        alt={panelist.panelist_name}
                                                        onError={(e) => {
                                                            e.target.src = OWWLogo; // Fallback image
                                                        }}
                                                    />
                                                    <Typography variant="subtitle1" sx={{
                                                        fontWeight: 600,
                                                        color: '#2D4356'
                                                    }}>
                                                        {panelist.panelist_name}
                                                    </Typography>
                                                    {panelist.panelist_designation && (
                                                        <Typography variant="body2" sx={{
                                                            color: '#00B8A9',
                                                            fontWeight: 500,
                                                            mt: 0.5
                                                        }}>
                                                            {panelist.panelist_designation}
                                                        </Typography>
                                                    )}
                                                    {panelist.panelist_company && (
                                                        <Typography variant="body2" sx={{
                                                            color: '#666',
                                                            fontSize: '0.8rem',
                                                            mt: 0.5
                                                        }}>
                                                            {panelist.panelist_company}
                                                        </Typography>
                                                    )}
                                                </PanelistCard>
                                            ))}
                                        </PanelistGrid>
                                    </PanelistSection>
                                )}
                            </CardContent>
                        </OpeningCeremonyCard>
                    ) : (
                        <SessionCard key={index}>
                            {session.session_heading && (
                                <SessionHeader>
                                    <Typography sx={{
                                        fontSize: { xs: '0.9rem', sm: '1.1rem' },
                                        fontWeight: 500,
                                        color: '#fff',
                                        textAlign: 'left',
                                        whiteSpace: 'nowrap'
                                    }}>
                                        {session.session_heading}
                                    </Typography>
                                </SessionHeader>
                            )}
                            <CardContent sx={{ p: 3 }}>
                                <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
                                    <Box sx={{ width: '30%' }}>
                                        <Box
                                            component="img"
                                            src={session.session_image}
                                            sx={{
                                                width: '100%',
                                                aspectRatio: '1/1',
                                                borderRadius: '12px',
                                                objectFit: 'cover'
                                            }}
                                        />
                                    </Box>
                                    <Box sx={{ width: '70%' }}>
                                        {session.session_name && <Chip label={session.session_name} color="primary" size="small" />}
                                        <SessionTitle>
                                            {session.session_title}
                                        </SessionTitle>
                                        <SessionTime>
                                            {session.session_time}
                                        </SessionTime>
                                    </Box>
                                </Box>
                                <Box sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'flex-start'
                                }}>
                                    {/* Chair Details (70% width) */}
                                    <Box sx={{ width: '70%' }}>
                                        <ChairLabel>
                                            {session.session_chairedBy}
                                        </ChairLabel>
                                        <ChairName>
                                            {session.session_chared_name}
                                        </ChairName>
                                        <ChairDetails>
                                            {session.session_chaired_designation}
                                        </ChairDetails>
                                        <ChairDetails>
                                            {session.session_chaired_company}
                                        </ChairDetails>
                                    </Box>

                                    {/* Add to Agenda Button (30% width) */}
                                    <Box sx={{
                                        width: '30%',
                                        display: 'flex',
                                        justifyContent: 'flex-end',
                                        alignItems: 'flex-end'
                                    }}>
                                        <IconButton onClick={() => toggleAgendaItem(session)}>
                                            {isSessionSaved(session.id) ? (
                                                <BookmarkAddedIcon fontSize='large' color='error' />
                                            ) : (
                                                <BookmarkAddIcon fontSize='large' color='primary' />
                                            )}
                                        </IconButton>
                                    </Box>

                                </Box>
                                {session.session_panelist && session.session_panelist.length > 0 && (
                                    <PanelistSection>

                                        <PanelistGrid>
                                            {session.session_panelist.map((panelist, panelIndex) => (
                                                <PanelistCard key={`${panelIndex}-${panelist.panelist_name}`}>
                                                    <Typography variant="h6" sx={{
                                                        color: '#2D4356',
                                                        fontWeight: 600,
                                                        mb: 2
                                                    }}>
                                                        {panelist.panelist_by}
                                                    </Typography>
                                                    <PanelistImage
                                                        src={panelist.panelist_image}
                                                        alt={panelist.panelist_name}
                                                        onError={(e) => {
                                                            e.target.src = OWWLogo; // Fallback image
                                                        }}
                                                    />
                                                    <Typography variant="subtitle1" sx={{
                                                        fontWeight: 600,
                                                        color: '#2D4356'
                                                    }}>
                                                        {panelist.panelist_name}
                                                    </Typography>
                                                    {panelist.panelist_designation && (
                                                        <Typography variant="body2" sx={{
                                                            color: '#00B8A9',
                                                            fontWeight: 500,
                                                            mt: 0.5
                                                        }}>
                                                            {panelist.panelist_designation}
                                                        </Typography>
                                                    )}
                                                    {panelist.panelist_company && (
                                                        <Typography variant="body2" sx={{
                                                            color: '#666',
                                                            fontSize: '0.8rem',
                                                            mt: 0.5
                                                        }}>
                                                            {panelist.panelist_company}
                                                        </Typography>
                                                    )}
                                                </PanelistCard>
                                            ))}
                                        </PanelistGrid>
                                    </PanelistSection>
                                )}
                            </CardContent>
                        </SessionCard>
                    )
                ))}
            </Box>
        </>
    );
};

export default AgendaUI;